'use strict';

/**
 * Important Project Files — data/logic provider (navigator + download + entitlement-gated management).
 *
 * The feature already exists server-side: two Meadow entities, ProjectFileFolder (nested via
 * ParentIDProjectFileFolder, project-scoped) and ProjectFile (a pointer into a folder at a FileType, carrying
 * IDObservation + IDArtifact + ArtifactVersion). This provider loads a whole project's folders + files in two
 * flat reads and assembles the nested tree in memory — the same shape the reference client
 * (web/providers/ProjectFileProvider.getProjectFilesDataStructure) builds, ported off pict.EntityProvider onto
 * this app's HeadlightRestClient. Standalone project-scoped screen, mirroring the Sample Plan front door.
 *
 * Read paths (everyone with Project-Read): load + tree + front door + per-file download of the current version.
 * Management (entitlement 'manage-important-files' → ProjectFile/ProjectFileFolder CRUD, gated by canManage()):
 * create/rename/move/delete folders, and rename/move/remove files — all generic Meadow CRUD (the bespoke
 * ImportantProjectFile endpoints are GET-only), full-record PUTs, soft-deletes, non-empty folders blocked (no
 * server cascade); plus upload (new file → Observation of the MIME's type + its Artifact binary → ProjectFile
 * pointer); plus a versions view (list + download any version — every version 1..Artifact.Version has bytes at
 * Artifact/Media/{id}/{v}; a new version is an upload to version N+1). Size visualization arrives in a later phase.
 */

const libPictProvider = require('pict-provider');

// Stored FileType carries a legacy prefix for everything but plain files ('Legacy Image' / 'Legacy Video' /
// 'Legacy Audio'); normalize to the clean taxonomy the UI shows + icons off.
const FILE_TYPES = [ 'File', 'Image', 'Video', 'Audio' ];

// getBinaryBlob returns an UNTYPED Blob, and stored names sometimes lack an extension; map the observation's
// FileMimeType (or, failing that, the FileType tier) to a sensible download extension.
const MIME_EXTENSIONS = {
	'application/pdf': 'pdf',
	'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/heic': 'heic', 'image/webp': 'webp', 'image/tiff': 'tiff', 'image/bmp': 'bmp',
	'video/mp4': 'mp4', 'video/quicktime': 'mov', 'video/x-msvideo': 'avi', 'video/webm': 'webm', 'video/mpeg': 'mpeg',
	'audio/mpeg': 'mp3', 'audio/mp3': 'mp3', 'audio/wav': 'wav', 'audio/x-wav': 'wav', 'audio/mp4': 'm4a', 'audio/aac': 'aac', 'audio/ogg': 'ogg',
	'text/plain': 'txt', 'text/csv': 'csv', 'application/json': 'json', 'application/zip': 'zip',
	'application/msword': 'doc', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
	'application/vnd.ms-excel': 'xls', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};
const DEFAULT_TYPE_EXTENSIONS = { Image: 'jpg', Video: 'mp4', Audio: 'mp3', File: '' };

// The artifact-type name inside an observation's Details differs from the datum key for plain files: a 'File'
// observation carries a 'Data_File' artifact (the server's Data_File manifest supplies AllowedMimeTypes /
// DisallowedExtensions and validates it — sending the datum key 'File' instead leaves the artifact Invalid=1).
// The legacy media types use their datum name as the artifact type as-is (verified against real observations).
const ARTIFACT_TYPE_FOR_DATUM = { File: 'Data_File', Image: 'Image', Video: 'Video', Audio: 'Audio' };

class ImportantFilesProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._idProject = 0;
		this._tree = [];
		this._rawFolders = {};   // IDProjectFileFolder -> raw record (full-record PUTs need every column)
		this._rawFiles = {};     // IDProjectFile -> raw record
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	/** The screen's AppData-backed state; slots + title are derived from it in the view's onBeforeRender. */
	get workspaceState()
	{
		if (!this.pict.AppData.ImportantFiles)
		{
			this.pict.AppData.ImportantFiles = { Loading: false, Message: '', HasModel: false, ProjectName: '', IDProject: 0, FileCount: 0, FolderCount: 0 };
		}
		return this.pict.AppData.ImportantFiles;
	}

	get tree() { return this._tree; }
	get idProject() { return this._idProject; }

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)));
		});
	}

	_render()
	{
		if (this.pict.views['ImportantFiles']) { this.pict.views['ImportantFiles'].render(); }
	}

	/** 'Legacy Image' -> 'Image'; '' -> 'File'; anything unknown falls back to 'File'. */
	_normalizeFileType(pFileType)
	{
		const tmpType = String(pFileType || '').replace(/^Legacy\s+/i, '').trim();
		return FILE_TYPES.indexOf(tmpType) >= 0 ? tmpType : 'File';
	}

	// --- Management entitlement (Phase 3) -----------------------------------------------------------

	get permissions() { return this.pict.fable.EffectivePermissionsService; }

	/**
	 * Server-authoritative capability check, fail-CLOSED for management (unlike the nav-visibility gate, which
	 * fails open): no permissions service or an unloaded capability map → false, so management affordances never
	 * flash before we know the user's grants. Super-users pass (the service bypasses for them). The server stays
	 * the real authority — this only decides what to show.
	 */
	can(pEntity, pAction)
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.can !== 'function') || !tmpPermissions.currentUserReady()) { return false; }
		return tmpPermissions.can(pEntity, pAction);
	}

	/**
	 * May the current user MANAGE important files (create/rename/move folders, add/remove files)? The
	 * 'manage-important-files' entitlement (Task-Catalog) grants the ProjectFile + ProjectFileFolder CRUD bundle
	 * together, so any one of them signals management; individual affordances in later phases still check their
	 * own specific capability. Viewers without the entitlement stay in the read-only navigator.
	 * @returns {Boolean}
	 */
	canManage()
	{
		return this.can('ProjectFile', 'Create')
			|| this.can('ProjectFile', 'Update')
			|| this.can('ProjectFile', 'Delete')
			|| this.can('ProjectFileFolder', 'Create')
			|| this.can('ProjectFileFolder', 'Update')
			|| this.can('ProjectFileFolder', 'Delete');
	}

	// --- Front door ---------------------------------------------------------------------------------

	/**
	 * Search the customer's projects by name for the front-door picker (mirrors the Sample Plan front door).
	 * @param {String} pTerm
	 * @returns {Promise<Array<{IDProject:Number, Name:String}>>}
	 */
	async searchProjectsAsync(pTerm)
	{
		const tmpTerm = String(pTerm == null ? '' : pTerm).replace(/~/g, '').trim();
		let tmpFilter = 'FBV~Deleted~EQ~0';
		if (tmpTerm) { tmpFilter = `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`; }
		const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/${tmpFilter}/0/100`).catch(() => []);
		// The Lite projection returns the display name under `Value` (the filter still runs on the real Name column).
		return (Array.isArray(tmpRows) ? tmpRows : [])
			.map((pRow) => ({ IDProject: pRow.IDProject, Name: pRow.Value || pRow.Name || pRow.Description || `Project ${pRow.IDProject}` }))
			.sort((pA, pB) => (pA.Name.toLowerCase() < pB.Name.toLowerCase() ? -1 : (pA.Name.toLowerCase() > pB.Name.toLowerCase() ? 1 : 0)));
	}

	/** Reset to the front-door project picker (the no-ID route + the "Choose another project" button). State only;
	 *  the route's _renderRouteView does the paint. */
	resetToPicker()
	{
		const tmpState = this.workspaceState;
		tmpState.Loading = false;
		tmpState.Message = '';
		tmpState.HasModel = false;
		tmpState.ProjectName = '';
		tmpState.IDProject = 0;
		this._idProject = 0;
		this._tree = [];
	}

	// --- Loading ------------------------------------------------------------------------------------

	/**
	 * Load a project's important files: two flat Meadow reads (folders + files) -> in-memory nested tree, then
	 * re-render. Mirrors the Sample Plan loadAsync lifecycle (Loading -> HasModel).
	 * @param {Number} pIDProject
	 */
	async loadAsync(pIDProject)
	{
		const tmpState = this.workspaceState;
		this._idProject = pIDProject;
		this._tree = [];
		tmpState.Loading = true;
		tmpState.Message = '';
		tmpState.HasModel = false;
		tmpState.IDProject = pIDProject;
		this._render();

		try
		{
			const tmpFolderFilter = `FBV~IDProject~EQ~${pIDProject}~FBV~Deleted~EQ~0`;
			const tmpFileFilter = `FBV~IDProject~EQ~${pIDProject}~FBV~Deleted~EQ~0`;
			const [ tmpFolders, tmpFiles, tmpProject ] = await Promise.all(
				[
					this._getJSON(`ProjectFileFolders/FilteredTo/${tmpFolderFilter}/0/5000`).catch(() => []),
					this._getJSON(`ProjectFiles/FilteredTo/${tmpFileFilter}/0/10000`).catch(() => []),
					this._getJSON(`Project/${pIDProject}`).catch(() => null),
				]);

			this._tree = this._buildTree(Array.isArray(tmpFolders) ? tmpFolders : [], Array.isArray(tmpFiles) ? tmpFiles : []);
			tmpState.FolderCount = (Array.isArray(tmpFolders) ? tmpFolders.length : 0);
			tmpState.FileCount = (Array.isArray(tmpFiles) ? tmpFiles.length : 0);
			tmpState.ProjectName = (tmpProject && (tmpProject.Name || tmpProject.Description)) || `Project ${pIDProject}`;
			tmpState.HasModel = true;
			tmpState.Loading = false;
			this._render();
		}
		catch (pError)
		{
			this.pict.log.error(`ImportantFiles: failed to load project ${pIDProject}: ${pError && pError.message ? pError.message : pError}`, pError);
			tmpState.Loading = false;
			tmpState.HasModel = false;
			tmpState.Message = 'Could not load this project’s files.';
			this._render();
		}
	}

	/**
	 * Merge the flat folder + file rows into a nested tree (folders wired by ParentIDProjectFileFolder; files
	 * dropped into their folder; folder-0 files collected into a synthetic read-only "Customer Files" root).
	 * Node shape: { Name, IsFolder, ID, RowID, Date, Children?, IsCustomer?, ParentID? } for folders and
	 * { Name, IsFolder:false, ID, RowID, Date, FileType, IDObservation, IDArtifact, ArtifactVersion } for files.
	 * @returns {Array<Object>} root nodes, sorted (folders first, then files, each by name).
	 */
	_buildTree(pFolders, pFiles)
	{
		const tmpMap = {};
		const tmpRoots = [];
		// Keep the raw records around, keyed by id — full-record PUTs (rename/move) need every column.
		this._rawFolders = {};
		this._rawFiles = {};

		for (let i = 0; i < pFolders.length; i++)
		{
			const tmpFolder = pFolders[i];
			this._rawFolders[tmpFolder.IDProjectFileFolder] = tmpFolder;
			tmpMap[tmpFolder.IDProjectFileFolder] = (
				{
					Name: tmpFolder.Name, IsFolder: true, IsCustomer: false,
					ID: tmpFolder.IDProjectFileFolder, ParentID: tmpFolder.ParentIDProjectFileFolder,
					Date: tmpFolder.UpdateDate, Children: [],
					RowID: `Folder_${tmpFolder.IDProjectFileFolder}`,
				});
		}
		// Wire each folder under its parent; a folder whose parent is 0 (or missing) is a root.
		Object.keys(tmpMap).forEach((pKey) =>
		{
			const tmpNode = tmpMap[pKey];
			if (tmpNode.ParentID > 0 && tmpMap[tmpNode.ParentID]) { tmpMap[tmpNode.ParentID].Children.push(tmpNode); }
			else { tmpRoots.push(tmpNode); }
		});

		let tmpCustomerFolder = null;
		for (let i = 0; i < pFiles.length; i++)
		{
			const tmpFile = pFiles[i];
			this._rawFiles[tmpFile.IDProjectFile] = tmpFile;
			const tmpFileNode = (
				{
					Name: tmpFile.Name, IsFolder: false,
					ID: tmpFile.IDProjectFile, IDObservation: tmpFile.IDObservation,
					IDArtifact: tmpFile.IDArtifact, ArtifactVersion: tmpFile.ArtifactVersion,
					FileType: this._normalizeFileType(tmpFile.FileType), Date: tmpFile.UpdateDate,
					RowID: `File_${tmpFile.IDProjectFile}`,
				});
			if (tmpMap[tmpFile.IDProjectFileFolder])
			{
				tmpMap[tmpFile.IDProjectFileFolder].Children.push(tmpFileNode);
			}
			else if (Number(tmpFile.IDProjectFileFolder) === 0)
			{
				// Files with no folder live in a synthetic read-only "Customer Files" root bucket (reference behavior).
				if (!tmpCustomerFolder)
				{
					tmpCustomerFolder = { Name: 'Customer Files', IsFolder: true, IsCustomer: true, ID: 0, ParentID: 0, Date: tmpFile.UpdateDate, Children: [], RowID: 'Folder_0' };
					tmpRoots.push(tmpCustomerFolder);
				}
				tmpCustomerFolder.Children.push(tmpFileNode);
			}
			else
			{
				this.pict.log.warn(`ImportantFiles: file ${tmpFile.IDProjectFile} references missing folder ${tmpFile.IDProjectFileFolder}; skipped.`);
			}
		}

		this._sortNodes(tmpRoots);
		return tmpRoots;
	}

	/** Recursively order a sibling list: folders before files, each alphabetically (case-insensitive). Phase-1 default sort. */
	_sortNodes(pNodes)
	{
		pNodes.sort((pA, pB) =>
		{
			if (pA.IsFolder !== pB.IsFolder) { return pA.IsFolder ? -1 : 1; }
			const tmpA = String(pA.Name || '').toLowerCase();
			const tmpB = String(pB.Name || '').toLowerCase();
			return tmpA < tmpB ? -1 : (tmpA > tmpB ? 1 : 0);
		});
		pNodes.forEach((pNode) => { if (pNode.Children && pNode.Children.length) { this._sortNodes(pNode.Children); } });
	}
	// --- Download (Phase 2) -------------------------------------------------------------------------

	/** Find a tree node (folder or file) by its RowID. */
	findNode(pRowID)
	{
		let tmpFound = null;
		const fWalk = (pNodes) =>
		{
			for (let i = 0; i < pNodes.length; i++)
			{
				if (tmpFound) { return; }
				if (pNodes[i].RowID === pRowID) { tmpFound = pNodes[i]; return; }
				if (pNodes[i].Children && pNodes[i].Children.length) { fWalk(pNodes[i].Children); }
			}
		};
		fWalk(this._tree || []);
		return tmpFound;
	}

	_getBlob(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getBinaryBlob(pURL, (pError, pBlob) => (pError ? reject(pError) : resolve(pBlob)));
		});
	}

	/** True when the filename already carries a plausible extension (so we don't need the observation to name it). */
	_hasExtension(pName) { return /\.[A-Za-z0-9]{1,8}$/.test(String(pName == null ? '' : pName).trim()); }

	/** A ProjectFile's own concrete artifact-version pin (>= 0), or null when unset/negative. The server treats a
	 *  negative ArtifactVersion as "track the artifact's current version" and everything >= 0 (including 0) as a
	 *  real pin, so 0 must be preserved — never coerced to 1. */
	_pinnedVersion(pNode)
	{
		const tmpVersion = pNode ? pNode.ArtifactVersion : undefined;
		if (tmpVersion === undefined || tmpVersion === null || tmpVersion === '') { return null; }
		const tmpNumber = Number(tmpVersion);
		return (Number.isFinite(tmpNumber) && tmpNumber >= 0) ? tmpNumber : null;
	}

	/**
	 * Fast path: a same-origin media URL the browser streams straight to disk via <a download> (ambient session
	 * cookie), with no round-trip and no in-memory blob — used whenever the node already pins a concrete artifact
	 * + version and its name carries an extension. Returns null when we must resolve via the observation (legacy
	 * rows with IDArtifact 0 / unpinned version / no extension) or when the API base is cross-origin (a download
	 * anchor + cookie aren't honored there — fall back to the authenticated blob fetch).
	 * @returns {{ url: String, filename: String } | null}
	 */
	directDownloadURL(pNode)
	{
		if (!pNode || pNode.IsFolder) { return null; }
		const tmpID = Number(pNode.IDArtifact);
		const tmpVersion = this._pinnedVersion(pNode);
		if (!(tmpID > 0) || (tmpVersion === null) || !this._hasExtension(pNode.Name)) { return null; }
		const tmpURL = this.restClient.formatUrl(`Artifact/Media/${tmpID}/${tmpVersion}`);
		// Only a same-origin URL streams via a plain download anchor with the ambient session cookie.
		const tmpSameOrigin = (tmpURL.charAt(0) === '/') || ((typeof window !== 'undefined') && (tmpURL.indexOf(window.location.origin) === 0));
		return tmpSameOrigin ? { url: tmpURL, filename: pNode.Name } : null;
	}

	/**
	 * Robust path: resolve a file node to a downloadable Blob + filename + mime, fetching the bytes through the
	 * authenticated rest client. The ProjectFile row's own IDArtifact + ArtifactVersion are authoritative (matching
	 * the server: a pinned version >= 0 wins; the observation artifact is only the fallback); the live Observation's
	 * Details.File[0] fills in a missing artifact/version and the real filename + mime. `getBinaryBlob` returns an
	 * untyped Blob, so we re-wrap it with the resolved mime. Used for legacy rows and the cross-origin case.
	 * @param {Object} pNode a file node from the tree
	 * @returns {Promise<{ blob: Blob, filename: String, mimetype: String }>}
	 */
	async resolveDownloadAsync(pNode)
	{
		if (!pNode || pNode.IsFolder) { throw new Error('Not a downloadable file.'); }

		let tmpIDArtifact = (Number(pNode.IDArtifact) > 0) ? Number(pNode.IDArtifact) : null;
		let tmpVersion = this._pinnedVersion(pNode);
		let tmpFileName = pNode.Name;
		let tmpMime = '';

		// Fetch the observation only when we still need something from it: the artifact id, the version, or a
		// filename with an extension. A pinned node with an extension-bearing name needs no round-trip.
		if (pNode.IDObservation && ((tmpIDArtifact === null) || (tmpVersion === null) || !this._hasExtension(tmpFileName)))
		{
			const tmpObservation = await this._getJSON(`Observation/${pNode.IDObservation}`).catch(() => null);
			const tmpFile = tmpObservation && tmpObservation.Details && tmpObservation.Details.File && tmpObservation.Details.File[0];
			if (tmpFile)
			{
				if ((tmpIDArtifact === null) && tmpFile.IDArtifact) { tmpIDArtifact = tmpFile.IDArtifact; }
				if ((tmpVersion === null) && (tmpFile.Version !== undefined) && (tmpFile.Version !== null)) { tmpVersion = tmpFile.Version; }
				if (tmpFile.Datum && tmpFile.Datum.FileName && !this._hasExtension(tmpFileName)) { tmpFileName = tmpFile.Datum.FileName; }
				if (tmpFile.Datum && tmpFile.Datum.FileMimeType) { tmpMime = tmpFile.Datum.FileMimeType; }
			}
		}

		if (!tmpIDArtifact) { throw new Error('This file has no downloadable artifact.'); }
		if (tmpVersion === null) { tmpVersion = 1; }   // last resort only — a real 0 pin is preserved above

		const tmpBlob = await this._getBlob(`Artifact/Media/${tmpIDArtifact}/${tmpVersion}`);
		const tmpTyped = (tmpMime && (typeof Blob !== 'undefined')) ? new Blob([ tmpBlob ], { type: tmpMime }) : tmpBlob;
		return { blob: tmpTyped, filename: this._ensureExtension(tmpFileName, tmpMime, pNode.FileType, tmpIDArtifact), mimetype: tmpMime };
	}

	/** Give the download a sensible filename + extension when the stored name lacks one. */
	_ensureExtension(pName, pMime, pFileType, pIDArtifact)
	{
		const tmpName = String(pName == null ? '' : pName).trim() || `file-${pIDArtifact || ''}`.replace(/-$/, '') || 'download';
		if (/\.[A-Za-z0-9]{1,8}$/.test(tmpName)) { return tmpName; }
		const tmpExt = MIME_EXTENSIONS[String(pMime || '').toLowerCase()] || DEFAULT_TYPE_EXTENSIONS[pFileType] || '';
		return tmpExt ? `${tmpName}.${tmpExt}` : tmpName;
	}

	// --- Mutations (Phase 4: folder + file management) ----------------------------------------------

	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_deleteEntity(pEntity, pID) { return new Promise((resolve, reject) => this.restClient.deleteEntity(pEntity, pID, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }

	/**
	 * Re-fetch the project's folders + files and rebuild the tree WITHOUT the full-screen loading flash (unlike
	 * loadAsync). Used after a mutation so the tree updates in place while the header + collapse state stay put.
	 */
	async _reloadTreeAsync()
	{
		const tmpState = this.workspaceState;
		const tmpFilter = `FBV~IDProject~EQ~${this._idProject}~FBV~Deleted~EQ~0`;
		const [ tmpFolders, tmpFiles ] = await Promise.all(
			[
				this._getJSON(`ProjectFileFolders/FilteredTo/${tmpFilter}/0/5000`).catch(() => []),
				this._getJSON(`ProjectFiles/FilteredTo/${tmpFilter}/0/10000`).catch(() => []),
			]);
		this._tree = this._buildTree(Array.isArray(tmpFolders) ? tmpFolders : [], Array.isArray(tmpFiles) ? tmpFiles : []);
		tmpState.FolderCount = (Array.isArray(tmpFolders) ? tmpFolders.length : 0);
		tmpState.FileCount = (Array.isArray(tmpFiles) ? tmpFiles.length : 0);
		this._render();
	}

	/** Guard: every write must clear the management gate (the server is the real authority, but fail fast + friendly). */
	_assertCanManage()
	{
		if (!this.canManage()) { throw new Error('You don’t have permission to manage this project’s important files.'); }
	}

	/** Real (non-synthetic) folders as a flat list for a move-target picker, each with an indented path label.
	 *  Optionally excludes a folder + its whole subtree (so a folder can't be moved into itself/a descendant). */
	folderChoices(pExcludeID)
	{
		const tmpExclude = this._descendantIDs(pExcludeID);
		if (pExcludeID) { tmpExclude[pExcludeID] = true; }
		const tmpList = [];
		const fWalk = (pNodes, pDepth) =>
		{
			pNodes.forEach((pNode) =>
			{
				if (!pNode.IsFolder || pNode.IsCustomer) { return; }   // the synthetic 'Customer Files' bucket isn't a real folder
				if (tmpExclude[pNode.ID]) { return; }
				tmpList.push({ ID: pNode.ID, Name: pNode.Name, Depth: pDepth });
				fWalk(pNode.Children || [], pDepth + 1);
			});
		};
		fWalk(this._tree || [], 0);
		return tmpList;
	}

	/** Map of a folder's descendant folder IDs (for move cycle-prevention). */
	_descendantIDs(pFolderID)
	{
		const tmpResult = {};
		const tmpNode = pFolderID ? this.findNode(`Folder_${pFolderID}`) : null;
		if (!tmpNode) { return tmpResult; }
		const fWalk = (pNodes) => pNodes.forEach((pChild) =>
		{
			if (pChild.IsFolder) { tmpResult[pChild.ID] = true; fWalk(pChild.Children || []); }
		});
		fWalk(tmpNode.Children || []);
		return tmpResult;
	}

	/** Create a folder (POST). ParentID 0 = a root folder. Server stamps IDCustomer/GUID/audit; IDProject drives
	 *  the membership check. */
	async createFolderAsync(pName, pParentID)
	{
		this._assertCanManage();
		const tmpName = String(pName == null ? '' : pName).trim();
		if (!tmpName) { throw new Error('A folder name is required.'); }
		await this._postJSON('ProjectFileFolder', { Name: tmpName, Description: '', ParentIDProjectFileFolder: Number(pParentID) || 0, IDProject: this._idProject });
		await this._reloadTreeAsync();
	}

	async renameFolderAsync(pNode, pNewName)
	{
		this._assertCanManage();
		const tmpName = String(pNewName == null ? '' : pNewName).trim();
		if (!tmpName) { throw new Error('A folder name is required.'); }
		const tmpRaw = this._rawFolders[pNode.ID];
		if (!tmpRaw) { throw new Error('Folder not found — refresh and try again.'); }
		if (tmpName === tmpRaw.Name) { return; }
		await this._putJSON('ProjectFileFolder', Object.assign({}, tmpRaw, { Name: tmpName }));
		await this._reloadTreeAsync();
	}

	async moveFolderAsync(pNode, pNewParentID)
	{
		this._assertCanManage();
		const tmpRaw = this._rawFolders[pNode.ID];
		if (!tmpRaw) { throw new Error('Folder not found — refresh and try again.'); }
		const tmpNewParent = Number(pNewParentID) || 0;
		if (tmpNewParent === pNode.ID) { throw new Error('A folder can’t be moved into itself.'); }
		if (this._descendantIDs(pNode.ID)[tmpNewParent]) { throw new Error('A folder can’t be moved into one of its own subfolders.'); }
		if (tmpNewParent === (tmpRaw.ParentIDProjectFileFolder || 0)) { return; }
		await this._putJSON('ProjectFileFolder', Object.assign({}, tmpRaw, { ParentIDProjectFileFolder: tmpNewParent }));
		await this._reloadTreeAsync();
	}

	/** Delete a folder (soft-delete). Blocks a non-empty folder — the server has no cascade, so its contents would
	 *  be orphaned; the caller must empty it first. */
	async deleteFolderAsync(pNode)
	{
		this._assertCanManage();
		if (pNode.Children && pNode.Children.length) { throw new Error('This folder isn’t empty. Move or remove everything inside it first.'); }
		await this._deleteEntity('ProjectFileFolder', pNode.ID);
		await this._reloadTreeAsync();
	}

	async renameFileAsync(pNode, pNewName)
	{
		this._assertCanManage();
		const tmpName = String(pNewName == null ? '' : pNewName).trim();
		if (!tmpName) { throw new Error('A file name is required.'); }
		const tmpRaw = this._rawFiles[pNode.ID];
		if (!tmpRaw) { throw new Error('File not found — refresh and try again.'); }
		if (tmpName === tmpRaw.Name) { return; }
		await this._putJSON('ProjectFile', Object.assign({}, tmpRaw, { Name: tmpName }));
		await this._reloadTreeAsync();
	}

	/** Move a file to another folder (ID 0 = top level / out of any folder). */
	async moveFileAsync(pNode, pNewFolderID)
	{
		this._assertCanManage();
		const tmpRaw = this._rawFiles[pNode.ID];
		if (!tmpRaw) { throw new Error('File not found — refresh and try again.'); }
		const tmpNewFolder = Number(pNewFolderID) || 0;
		if (tmpNewFolder === (tmpRaw.IDProjectFileFolder || 0)) { return; }
		await this._putJSON('ProjectFile', Object.assign({}, tmpRaw, { IDProjectFileFolder: tmpNewFolder }));
		await this._reloadTreeAsync();
	}

	/** Remove a file from the project's important files (soft-delete the ProjectFile pointer). The underlying
	 *  Observation + its artifact are untouched — this only unlists it here. */
	async removeFileAsync(pNode)
	{
		this._assertCanManage();
		await this._deleteEntity('ProjectFile', pNode.ID);
		await this._reloadTreeAsync();
	}

	// --- Upload (Phase 5: new file -> Observation + Artifact -> ProjectFile) -------------------------

	_postBinary(pURL, pFile, pOnProgress)
	{
		return new Promise((resolve, reject) => this.restClient.postBinary(pURL, pFile, pFile.type || 'application/octet-stream', (pErr, pBody) => (pErr ? reject(pErr) : resolve(pBody)), pOnProgress));
	}

	/** Browser File MIME -> Headlight ObservationType (media carry the 'Legacy' prefix; everything else is a plain
	 *  'File'). The datum key is that type without the prefix ('Image'/'Video'/'Audio'/'File'). */
	_observationTypeForMime(pMime)
	{
		const tmpMime = String(pMime || '').toLowerCase();
		if (tmpMime.indexOf('image/') === 0) { return 'Legacy Image'; }
		if (tmpMime.indexOf('video/') === 0) { return 'Legacy Video'; }
		if (tmpMime.indexOf('audio/') === 0) { return 'Legacy Audio'; }
		return 'File';
	}

	/**
	 * Upload a new file into the important files: (1) create an Observation of the right type — the server
	 * provisions its Artifact and returns the id + version; (2) POST the raw bytes to that artifact; (3) create the
	 * ProjectFile pointer into the target folder. Mirrors the reference ObservationProvider pipeline, ported onto
	 * the rest client (no ObservationProvider/UploadTracking/Geo here; EXIF + geo enrichment are skipped). Does NOT
	 * reload the tree — the caller reloads once after a batch.
	 * @param {File} pFile a browser File (from an <input type=file>)
	 * @param {Number} pFolderID destination folder (0 = top level / not in a folder)
	 * @param {Function} [pOnProgress] postBinary progress callback
	 * @returns {Promise<{ IDObservation:Number, IDProjectFile:Number, Name:String }>}
	 */
	async uploadFileAsync(pFile, pFolderID, pOnProgress)
	{
		this._assertCanManage();
		if (!pFile) { throw new Error('No file selected.'); }
		const tmpObservationType = this._observationTypeForMime(pFile.type);
		const tmpDatumType = tmpObservationType.replace(/^Legacy\s+/, '');
		const tmpArtifactType = ARTIFACT_TYPE_FOR_DATUM[tmpDatumType] || tmpDatumType;
		const tmpFileName = pFile.name || `New ${tmpDatumType}`;

		const tmpObservation = {
			Name: tmpFileName, Description: '', Tags: [], ObservationType: tmpObservationType,
			Details: { [tmpDatumType]: [ { ArtifactType: tmpArtifactType, IDArtifact: 0, Datum: { FileName: tmpFileName, FileMimeType: pFile.type || '' } } ] },
			IDObservation: 0, IDProject: this._idProject,
			CaptureLatitude: '0.00000000', CaptureLongitude: '0.00000000',
			UserLatitude: '0.00000000', UserLongitude: '0.00000000',
			AlignmentStation: 0, AlignmentOffset: 0,
		};
		const tmpNewObs = await this._postJSON('Observation', tmpObservation);
		const tmpArtifact = tmpNewObs && tmpNewObs.Details && tmpNewObs.Details[tmpDatumType] && tmpNewObs.Details[tmpDatumType][0];
		if (!tmpNewObs || !tmpNewObs.IDObservation || !tmpArtifact || !tmpArtifact.IDArtifact)
		{
			throw new Error('Could not create the observation for this file.');
		}
		await this._postBinary(`Artifact/Media/${tmpArtifact.IDArtifact}/${tmpArtifact.Version}`, pFile, pOnProgress);
		const tmpProjectFile = await this._postJSON('ProjectFile',
			{
				Name: tmpFileName, Description: '', IDProject: this._idProject, IDProjectFileFolder: Number(pFolderID) || 0,
				IDObservation: tmpNewObs.IDObservation, IDArtifact: tmpArtifact.IDArtifact, ArtifactVersion: tmpArtifact.Version,
				FileType: tmpObservationType,
			});
		return { IDObservation: tmpNewObs.IDObservation, IDProjectFile: (tmpProjectFile && (tmpProjectFile.IDProjectFile || tmpProjectFile.ID)) || 0, Name: tmpFileName };
	}

	// --- Add EXISTING observation (create a ProjectFile pointer; no new Observation/Artifact) --------

	/** Map of IDObservation already referenced by a ProjectFile in this project — excluded from the add-picker so
	 *  a file isn't listed twice. */
	_usedObservationIDs()
	{
		const tmpUsed = {};
		Object.keys(this._rawFiles || {}).forEach((pKey) =>
		{
			const tmpFile = this._rawFiles[pKey];
			if (tmpFile && tmpFile.IDObservation) { tmpUsed[tmpFile.IDObservation] = true; }
		});
		return tmpUsed;
	}

	get importableCap() { return 500; }

	/**
	 * Load (and cache) the project's file/photo observations that can be added to the important files. One read of
	 * the most-recent observations (the collection read carries `Details`, so we resolve each artifact + keep only
	 * file-bearing rows), excluding observations already listed here. Filename search is client-side — the file's
	 * real name lives in the artifact `Datum.FileName`, not a server-filterable column (the observation `Name` is
	 * often a generic "New File"), so a server LIKE on Name would miss what the user actually sees. Call once when
	 * the picker opens; then `filterImportableObservations` narrows the cache per keystroke.
	 * @returns {Promise<{ total:Number, truncated:Boolean }>}
	 */
	async loadImportableObservationsAsync()
	{
		this._importableObservations = [];
		this._importableTruncated = false;
		if (!this._idProject) { return { total: 0, truncated: false }; }
		const tmpCap = this.importableCap;
		const tmpFilter = `FBV~IDProject~EQ~${this._idProject}~FBV~Deleted~EQ~0`;
		// Newest first; some deployments reject the sort token on this entity, so fall back unsorted.
		let tmpRows = await this._getJSON(`Observations/FilteredTo/${tmpFilter}~FSF~CreateDate~DESC~0/0/${tmpCap}`).catch(() => null);
		if (!Array.isArray(tmpRows)) { tmpRows = await this._getJSON(`Observations/FilteredTo/${tmpFilter}/0/${tmpCap}`).catch(() => []); }
		// We only fetched the most-recent page; there may be older file observations we didn't pull.
		this._importableTruncated = Array.isArray(tmpRows) && (tmpRows.length >= tmpCap);
		const tmpUsed = this._usedObservationIDs();
		const tmpResults = [];
		(Array.isArray(tmpRows) ? tmpRows : []).forEach((pObs) =>
		{
			if (!pObs || !pObs.IDObservation || tmpUsed[pObs.IDObservation]) { return; }
			const tmpSlot = this._firstFileArtifact(pObs.Details);
			if (!tmpSlot || !tmpSlot.IDArtifact) { return; }   // no file/photo artifact — can't be added
			const tmpName = (tmpSlot.Datum && tmpSlot.Datum.FileName) || pObs.Name || `Observation ${pObs.IDObservation}`;
			tmpResults.push(
				{
					IDObservation: pObs.IDObservation,
					Name: tmpName,
					ObservationType: pObs.ObservationType || '',
					FileType: this._normalizeFileType(pObs.ObservationType),
					Date: pObs.CreateDate || pObs.UpdateDate || null,
					IDArtifact: tmpSlot.IDArtifact,
					ArtifactVersion: ((tmpSlot.Version !== undefined) && (tmpSlot.Version !== null)) ? tmpSlot.Version : null,
					Mime: (tmpSlot.Datum && tmpSlot.Datum.FileMimeType) || '',
					// Precomputed lowercase haystack for the client-side name filter.
					Search: String(tmpName).toLowerCase(),
				});
		});
		this._importableObservations = tmpResults;
		return { total: tmpResults.length, truncated: this._importableTruncated };
	}

	/**
	 * Narrow the cached importable-observation rows by a filename substring (client-side; see
	 * loadImportableObservationsAsync). Capped so a huge project doesn't render thousands of rows at once.
	 * @param {String} pTerm
	 * @param {Number} [pLimit]
	 * @returns {{ rows:Array<Object>, total:Number, shown:Number, truncated:Boolean }}
	 */
	filterImportableObservations(pTerm, pLimit)
	{
		const tmpAll = this._importableObservations || [];
		const tmpTerm = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
		const tmpMatched = tmpTerm ? tmpAll.filter((pRow) => pRow.Search.indexOf(tmpTerm) >= 0) : tmpAll;
		const tmpLimit = pLimit || 150;
		return { rows: tmpMatched.slice(0, tmpLimit), total: tmpMatched.length, shown: Math.min(tmpMatched.length, tmpLimit), truncated: !!this._importableTruncated };
	}

	/**
	 * Add an EXISTING observation's file to the important files: create a ProjectFile pointer into pFolderID (0 =
	 * top level) referencing the observation's current file/media artifact. Unlike uploadFileAsync this creates NO
	 * new Observation/Artifact — it just lists a file that already exists. Uses the picker's hint when it carried
	 * the artifact (Details-bearing list); otherwise resolves from the live Observation. Reloads the tree unless
	 * pSkipReload (the picker adds many, then reloads once on close).
	 * @param {Number} pIDObservation
	 * @param {Number} pFolderID destination folder (0 = top level)
	 * @param {Object} [pHint] optional picker row { Name, ObservationType, IDArtifact, ArtifactVersion }
	 * @param {Boolean} [pSkipReload]
	 * @returns {Promise<{ IDProjectFile:Number, Name:String }>}
	 */
	async addExistingObservationAsync(pIDObservation, pFolderID, pHint, pSkipReload)
	{
		this._assertCanManage();
		const tmpID = Number(pIDObservation);
		if (!(tmpID > 0)) { throw new Error('No observation selected.'); }

		let tmpIDArtifact = (pHint && (Number(pHint.IDArtifact) > 0)) ? Number(pHint.IDArtifact) : 0;
		let tmpVersion = (pHint && (pHint.ArtifactVersion !== undefined) && (pHint.ArtifactVersion !== null) && (pHint.ArtifactVersion !== '')) ? Number(pHint.ArtifactVersion) : null;
		let tmpName = (pHint && pHint.Name) || '';
		let tmpObservationType = (pHint && pHint.ObservationType) || '';

		// Resolve from the live observation whenever the picker didn't already carry everything (Details-less list).
		if (!(tmpIDArtifact > 0) || (tmpVersion === null) || !tmpName || !tmpObservationType)
		{
			const tmpObservation = await this._getJSON(`Observation/${tmpID}`).catch(() => null);
			if (!tmpObservation) { throw new Error('Could not load that observation.'); }
			if (!tmpObservationType) { tmpObservationType = tmpObservation.ObservationType || 'File'; }
			const tmpSlot = this._firstFileArtifact(tmpObservation.Details);
			if (tmpSlot)
			{
				if (!(tmpIDArtifact > 0) && tmpSlot.IDArtifact) { tmpIDArtifact = tmpSlot.IDArtifact; }
				if ((tmpVersion === null) && (tmpSlot.Version !== undefined) && (tmpSlot.Version !== null)) { tmpVersion = Number(tmpSlot.Version); }
				if (!tmpName && tmpSlot.Datum && tmpSlot.Datum.FileName) { tmpName = tmpSlot.Datum.FileName; }
			}
			if (!tmpName) { tmpName = tmpObservation.Name || `Observation ${tmpID}`; }
		}

		if (!(tmpIDArtifact > 0)) { throw new Error('That observation has no file or photo to add.'); }
		if (tmpVersion === null) { tmpVersion = 1; }
		if (!tmpName) { tmpName = `Observation ${tmpID}`; }

		// Store FileType as the observation type (the same convention uploadFileAsync uses); the view normalizes it.
		const tmpProjectFile = await this._postJSON('ProjectFile',
			{
				Name: tmpName, Description: '', IDProject: this._idProject, IDProjectFileFolder: Number(pFolderID) || 0,
				IDObservation: tmpID, IDArtifact: tmpIDArtifact, ArtifactVersion: tmpVersion,
				FileType: tmpObservationType || 'File',
			});
		if (!pSkipReload) { await this._reloadTreeAsync(); }
		return { IDProjectFile: (tmpProjectFile && (tmpProjectFile.IDProjectFile || tmpProjectFile.ID)) || 0, Name: tmpName };
	}

	// --- Versions (Phase 6: download older versions) ------------------------------------------------

	/** The file/media artifact slot inside an observation's Details (File/Image/Video/Audio, else the first array). */
	_firstFileArtifact(pDetails)
	{
		if (!pDetails) { return null; }
		const tmpKeys = [ 'File', 'Image', 'Video', 'Audio' ];
		for (let i = 0; i < tmpKeys.length; i++) { if (pDetails[tmpKeys[i]] && pDetails[tmpKeys[i]][0]) { return pDetails[tmpKeys[i]][0]; } }
		const tmpAll = Object.keys(pDetails);
		for (let i = 0; i < tmpAll.length; i++) { if (Array.isArray(pDetails[tmpAll[i]]) && pDetails[tmpAll[i]][0]) { return pDetails[tmpAll[i]][0]; } }
		return null;
	}

	/**
	 * Resolve a file node's artifact reference: its IDArtifact, the CURRENT version (the live Observation's artifact
	 * slot is the source of truth; the node's own fields are the fallback), plus the real filename + mime + the
	 * current version's date. Every version 1..currentVersion has bytes at Artifact/Media/{id}/{v}.
	 * @returns {Promise<{ idArtifact:Number, currentVersion:Number, fileName:String, mime:String, currentDate:(String|null) }>}
	 */
	async _resolveArtifactRef(pNode)
	{
		if (!pNode || pNode.IsFolder) { throw new Error('Not a file.'); }
		let tmpIDArtifact = (Number(pNode.IDArtifact) > 0) ? Number(pNode.IDArtifact) : null;
		let tmpVersion = null;
		let tmpFileName = pNode.Name;
		let tmpMime = '';
		let tmpDate = pNode.Date || null;
		if (pNode.IDObservation)
		{
			const tmpObservation = await this._getJSON(`Observation/${pNode.IDObservation}`).catch(() => null);
			const tmpSlot = tmpObservation && this._firstFileArtifact(tmpObservation.Details);
			if (tmpSlot)
			{
				if (tmpSlot.IDArtifact) { tmpIDArtifact = tmpSlot.IDArtifact; }
				if ((tmpSlot.Version !== undefined) && (tmpSlot.Version !== null)) { tmpVersion = Number(tmpSlot.Version); }
				if (tmpSlot.UpdateDate) { tmpDate = tmpSlot.UpdateDate; }
				if (tmpSlot.Datum && tmpSlot.Datum.FileName) { tmpFileName = tmpSlot.Datum.FileName; }
				if (tmpSlot.Datum && tmpSlot.Datum.FileMimeType) { tmpMime = tmpSlot.Datum.FileMimeType; }
			}
		}
		if (tmpVersion === null) { tmpVersion = this._pinnedVersion(pNode); }
		if (!tmpIDArtifact) { throw new Error('This file has no downloadable artifact.'); }
		if ((tmpVersion === null) || (tmpVersion < 1)) { tmpVersion = 1; }
		return { idArtifact: tmpIDArtifact, currentVersion: tmpVersion, fileName: tmpFileName, mime: tmpMime, currentDate: tmpDate };
	}

	/**
	 * List a file's versions, newest first. Each version 1..current is a downloadable blob; only the current
	 * version carries a reliable date (older per-version metadata isn't recorded — an extension point).
	 * @returns {Promise<{ idArtifact:Number, currentVersion:Number, fileName:String, mime:String, versions:Array }>}
	 */
	async listVersionsAsync(pNode)
	{
		const tmpRef = await this._resolveArtifactRef(pNode);
		const tmpVersions = [];
		for (let v = tmpRef.currentVersion; v >= 1; v--)
		{
			const tmpIsCurrent = (v === tmpRef.currentVersion);
			tmpVersions.push({ version: v, isCurrent: tmpIsCurrent, date: tmpIsCurrent ? tmpRef.currentDate : null });
		}
		return { idArtifact: tmpRef.idArtifact, currentVersion: tmpRef.currentVersion, fileName: tmpRef.fileName, mime: tmpRef.mime, versions: tmpVersions };
	}

	/** Download one specific version's bytes. Pass the cached ref (from listVersionsAsync) to skip a re-resolve.
	 *  Older versions get a ' (vN)' filename suffix so they don't collide with the current download on disk. */
	async downloadVersionAsync(pNode, pVersion, pRef)
	{
		const tmpRef = pRef || await this._resolveArtifactRef(pNode);
		const tmpBlob = await this._getBlob(`Artifact/Media/${tmpRef.idArtifact}/${pVersion}`);
		const tmpTyped = (tmpRef.mime && (typeof Blob !== 'undefined')) ? new Blob([ tmpBlob ], { type: tmpRef.mime }) : tmpBlob;
		const tmpBase = this._ensureExtension(tmpRef.fileName, tmpRef.mime, pNode.FileType, tmpRef.idArtifact);
		const tmpFilename = (Number(pVersion) === tmpRef.currentVersion) ? tmpBase : this._versionedFilename(tmpBase, pVersion);
		return { blob: tmpTyped, filename: tmpFilename };
	}

	/** 'report.pdf' + v2 -> 'report (v2).pdf'. */
	_versionedFilename(pName, pVersion)
	{
		const tmpMatch = String(pName || 'download').match(/^(.*?)(\.[A-Za-z0-9]{1,8})?$/);
		const tmpStem = (tmpMatch && tmpMatch[1]) || String(pName || 'download');
		const tmpExt = (tmpMatch && tmpMatch[2]) || '';
		return `${tmpStem} (v${pVersion})${tmpExt}`;
	}

}

module.exports = ImportantFilesProvider;
module.exports.default_configuration = { ProviderIdentifier: 'ImportantFiles' };
